import React from "react";
import { observer } from "mobx-react-lite";
import { Tooltip } from "@heroui/react";
import { clientsStore } from "@/stores/ClientsStore";
import Sparkline from "./Sparkline";
import { NoDataPlaceholder } from "@/features/general";

function formatPeriodLabel(month: Date): string {
  const now = new Date();
  const isCurrentMonth =
    month.getFullYear() === now.getFullYear() &&
    month.getMonth() === now.getMonth();
  if (isCurrentMonth) {
    const day = now.getDate();
    const monthShort = month.toLocaleString("ru-RU", { month: "long" });
    return `с 1 по ${day} ${monthShort} ${now.getFullYear()}`;
  }
  return month
    .toLocaleString("ru-RU", { month: "long", year: "numeric" })
    .replace(/\s*г\.?\s*$/i, "");
}

function formatPrevPeriodLabel(month: Date): string {
  const now = new Date();
  const isCurrentMonth =
    month.getFullYear() === now.getFullYear() &&
    month.getMonth() === now.getMonth();
  const prev = new Date(month.getFullYear(), month.getMonth() - 1, 1);
  if (isCurrentMonth) {
    const day = now.getDate();
    const prevMonthShort = prev.toLocaleString("ru-RU", { month: "long" });
    return `с 1 по ${day} ${prevMonthShort} ${prev.getFullYear()}`;
  }
  return prev
    .toLocaleString("ru-RU", { month: "long", year: "numeric" })
    .replace(/\s*г\.?\s*$/i, "");
}

function formatChange(value: number): string {
  const sign = value >= 0 ? "+" : "−";
  return `${sign}${Math.abs(value)}%`;
}

const MetricsSkeleton: React.FC = () => (
  <div className="cl-metrics">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="cl-metric-card cl-metric-card--skeleton" />
    ))}
  </div>
);

const MetricsPanel: React.FC = observer(() => {
  const { summary, isSummaryLoading, activeMonth } = clientsStore;

  if (isSummaryLoading || !summary) {
    return <MetricsSkeleton />;
  }

  const metrics = [
    {
      label: "Пользователи",
      value: summary.totalUsers.toLocaleString("ru-RU"),
      change: summary.totalUsersChange,
      sparkData: summary.charts?.totalBookings ?? [],
      color: "#6366f1",
      description: "Общее количество зарегистрированных клиентов на платформе",
    },
    {
      label: "Бронирования",
      value: summary.totalBookings.toLocaleString("ru-RU"),
      change: summary.totalBookingsChange,
      sparkData: summary.charts?.totalBookings ?? [],
      color: "#06b6d4",
      description: "Суммарное количество бронирований клиентов за период",
    },
    {
      label: "Через приложение",
      value: `${summary.appBookingsPct}%`,
      change: summary.appBookingsPctChange,
      sparkData: [],
      color: "#22c55e",
      description: "Доля бронирований, оформленных через мобильное приложение",
    },
    {
      label: "Активные пользователи",
      value: `${summary.activeUsersPct}%`,
      change: summary.activeUsersPctChange,
      sparkData: [],
      color: "#f59e0b",
      description:
        "Доля пользователей, совершивших хотя бы одно бронирование за период",
    },
  ];

  return (
    <div className="cl-metrics">
      {metrics.map((m) => (
        <div key={m.label} className="cl-metric-card">
          <div className="cl-metric-card__info">
            <span className="cl-metric-card__label">{m.label}</span>
            <Tooltip delay={300}>
              <Tooltip.Trigger>
                <span className="cl-metric-card__value cl-metric-card__value--tip">
                  {m.value}
                </span>
              </Tooltip.Trigger>
              <Tooltip.Content className="cl-metric-tooltip">
                <p className="cl-metric-tooltip__desc">{m.description}</p>
                <p className="cl-metric-tooltip__period">
                  Период: {formatPeriodLabel(activeMonth)}
                </p>
              </Tooltip.Content>
            </Tooltip>
            <Tooltip delay={300}>
              <Tooltip.Trigger>
                <span
                  className={`cl-metric-card__change cl-metric-card__change--tip ${
                    m.change >= 0
                      ? "cl-metric-card__change--positive"
                      : "cl-metric-card__change--negative"
                  }`}
                >
                  {formatChange(m.change)}
                </span>
              </Tooltip.Trigger>
              <Tooltip.Content className="cl-metric-tooltip">
                <p className="cl-metric-tooltip__desc">
                  Изменение относительно аналогичного периода прошлого месяца
                </p>
                <p className="cl-metric-tooltip__period">
                  Сравнение: {formatPrevPeriodLabel(activeMonth)}
                </p>
              </Tooltip.Content>
            </Tooltip>
          </div>
          {m.sparkData.length > 0 ? (
            <Sparkline data={m.sparkData} color={m.color} />
          ) : (
            <NoDataPlaceholder />
          )}
        </div>
      ))}
    </div>
  );
});

MetricsPanel.displayName = "MetricsPanel";
export default MetricsPanel;
